(() => {
  if (window.SeelenhauchProductDrawersV12) return;
  window.SeelenhauchProductDrawersV12 = true;

  let activeDrawer = null;
  let activeTrigger = null;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function mountDrawerToBody(drawer) {
    if (!drawer || drawer.dataset.shMounted === 'true') return;

    drawer.dataset.shMounted = 'true';
    drawer.dataset.shOriginalParentId =
      drawer.parentElement?.closest('[data-sh-product-details]')?.dataset.shBlockId || '';

    document.body.appendChild(drawer);
  }

  function mountAllDrawers(root = document) {
    root.querySelectorAll('[data-sh-drawer]').forEach(mountDrawerToBody);
  }

  function getDrawerFromTrigger(trigger) {
    const id = trigger?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function getPanels(drawer) {
    return Array.from(drawer.querySelectorAll('[data-sh-drawer-panel]'));
  }

  function closeDrawer(drawer = activeDrawer) {
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    getPanels(drawer).forEach((panel) => {
      panel.hidden = true;
    });

    document.documentElement.classList.remove('sh-product-drawer-open');
    document.body.classList.remove('sh-product-drawer-open');

    const triggerToRestore = activeTrigger;
    activeDrawer = null;
    activeTrigger = null;

    window.setTimeout(() => {
      if (triggerToRestore && document.documentElement.contains(triggerToRestore)) {
        triggerToRestore.focus({ preventScroll: true });
      }
    }, 0);
  }

  function openDrawer(trigger) {
    const drawer = getDrawerFromTrigger(trigger);
    if (!drawer) return;

    mountDrawerToBody(drawer);

    const key = trigger.dataset.shDrawerOpen;
    const panel = drawer.querySelector(`[data-sh-drawer-panel="${CSS.escape(key)}"]`);
    if (!panel) return;

    if (activeDrawer && activeDrawer !== drawer) {
      closeDrawer(activeDrawer);
    }

    getPanels(drawer).forEach((item) => {
      item.hidden = item !== panel;
    });

    const heading = drawer.querySelector('[data-sh-drawer-heading]');
    if (heading) {
      heading.textContent = trigger.dataset.shDrawerTitle || '';
    }

    activeDrawer = drawer;
    activeTrigger = trigger;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sh-product-drawer-open');
    document.body.classList.add('sh-product-drawer-open');

    const closeButton = drawer.querySelector('.sh-product-drawer__close');
    const panelEl = drawer.querySelector('.sh-product-drawer__panel');

    window.requestAnimationFrame(() => {
      (closeButton || panelEl)?.focus({ preventScroll: true });
    });
  }

  function trapFocus(event) {
    if (!activeDrawer || event.key !== 'Tab') return;

    const panel = activeDrawer.querySelector('.sh-product-drawer__panel');
    if (!panel) return;

    const focusables = Array.from(panel.querySelectorAll(focusableSelector))
      .filter((el) => !el.hidden && el.offsetParent !== null);

    if (!focusables.length) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function getDrawerForRoot(root) {
    const trigger = root?.querySelector('[data-sh-drawer-open][aria-controls]');
    if (!trigger) return null;

    return getDrawerFromTrigger(trigger);
  }

  function updateVariantFacts(root) {
    if (!root) return;

    const drawer = getDrawerForRoot(root);
    if (!drawer) return;

    const jsonNode = drawer.querySelector('[data-sh-variant-data]');
    if (!jsonNode) return;

    let variants;
    try {
      variants = JSON.parse(jsonNode.textContent);
    } catch (error) {
      return;
    }

    const productInfo = root.closest('product-info') || root.closest('.product');
    const idInput = productInfo?.querySelector('form[action*="/cart/add"] input[name="id"]');
    const variantId = Number(idInput?.value);

    if (!variantId) return;

    const variant = variants.find((item) => Number(item.id) === variantId);
    if (!variant) return;

    const skuRow = drawer.querySelector('[data-sh-sku-row]');
    const sku = drawer.querySelector('[data-sh-sku]');

    if (skuRow && sku) {
      sku.textContent = variant.sku || '';
      skuRow.hidden = !variant.sku;
    }

    const barcodeRow = drawer.querySelector('[data-sh-barcode-row]');
    const barcode = drawer.querySelector('[data-sh-barcode]');

    if (barcodeRow && barcode) {
      barcode.textContent = variant.barcode || '';
      barcodeRow.hidden = !variant.barcode;
    }
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-sh-drawer-open]');

    if (opener) {
      event.preventDefault();
      openDrawer(opener);
      return;
    }

    const closer = event.target.closest('[data-sh-drawer-close]');

    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('[data-sh-drawer]'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    trapFocus(event);
  });

  document.addEventListener('change', (event) => {
    const productInfo = event.target.closest('product-info') || event.target.closest('.product');
    if (!productInfo) return;

    window.setTimeout(() => {
      productInfo.querySelectorAll('[data-sh-product-details]').forEach(updateVariantFacts);
    }, 0);
  });

  document.addEventListener('shopify:section:load', (event) => {
    mountAllDrawers(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (activeTrigger && event.target.contains(activeTrigger)) {
      closeDrawer();
    }

    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    document.querySelectorAll('[data-sh-drawer]').forEach((drawer) => {
      if (drawer.id.includes(sectionId) && drawer.dataset.shMounted === 'true') {
        drawer.remove();
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAllDrawers());
  } else {
    mountAllDrawers();
  }
})();